import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Save, GripVertical, Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

const formSchema = z.object({
  categoryOrganization: z.object({
    enabled: z.boolean().default(false),
    categories: z.array(z.object({
      name: z.string(),
      maxItems: z.number().min(1).max(50).default(12),
      enabled: z.boolean().default(true),
      order: z.number().default(0),
    })).default([]),
  }),
});

interface CategoryConfig {
  name: string;
  maxItems: number;
  enabled: boolean;
  order: number;
}

const defaultCategories: CategoryConfig[] = [
  { name: 'Lançamento', maxItems: 12, enabled: true, order: 0 },
  { name: 'Na planta', maxItems: 12, enabled: true, order: 1 },
  { name: 'Em Construção', maxItems: 12, enabled: true, order: 2 },
  { name: 'Pronto para morar', maxItems: 12, enabled: true, order: 3 },
];

export default function StorefrontCategoryManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const { user } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      categoryOrganization: {
        enabled: false,
        categories: defaultCategories,
      },
    },
  });

  useEffect(() => {
    if (user?.id) {
      loadSettings();
    }
  }, [user?.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('user_storefront_settings')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const categoryOrganization = data.settings.categoryOrganization || {
          enabled: false,
          categories: defaultCategories,
        };

        form.reset({
          categoryOrganization,
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setSaving(true);

      // Get existing settings first
      const { data: existingData } = await supabase
        .from('user_storefront_settings')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      const existingSettings = existingData?.settings || {};

      // Merge with existing settings
      const settingsData = {
        ...existingSettings,
        categoryOrganization: values.categoryOrganization,
      };

      const { data: existingSettingsId } = await supabase
        .from('user_storefront_settings')
        .select('id')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (existingSettingsId) {
        const { error } = await supabase
          .from('user_storefront_settings')
          .update({
            settings: settingsData,
          })
          .eq('id', existingSettingsId.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_storefront_settings')
          .insert({
            user_id: user?.id,
            settings: settingsData,
          });

        if (error) throw error;
      }

      toast.success('Configurações salvas com sucesso');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const currentCategories = form.getValues('categoryOrganization.categories');
    const newCategories = [...currentCategories];
    const draggedItem = newCategories[draggedIndex];
    
    // Remove the dragged item
    newCategories.splice(draggedIndex, 1);
    // Insert it at the new position
    newCategories.splice(dropIndex, 0, draggedItem);
    
    // Update order values
    const updatedCategories = newCategories.map((cat, index) => ({
      ...cat,
      order: index,
    }));

    form.setValue('categoryOrganization.categories', updatedCategories);
    setDraggedIndex(null);
  };

  const updateCategoryMaxItems = (index: number, maxItems: number) => {
    const currentCategories = form.getValues('categoryOrganization.categories');
    const updatedCategories = [...currentCategories];
    updatedCategories[index] = { ...updatedCategories[index], maxItems };
    form.setValue('categoryOrganization.categories', updatedCategories);
  };

  const updateCategoryEnabled = (index: number, enabled: boolean) => {
    const currentCategories = form.getValues('categoryOrganization.categories');
    const updatedCategories = [...currentCategories];
    updatedCategories[index] = { ...updatedCategories[index], enabled };
    form.setValue('categoryOrganization.categories', updatedCategories);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const categories = form.watch('categoryOrganization.categories');
  const categoryOrganizationEnabled = form.watch('categoryOrganization.enabled');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Enable Category Organization */}
        <FormField
          control={form.control}
          name="categoryOrganization.enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Organização por Categorias
                </FormLabel>
                <FormDescription>
                  Organizar imóveis por categoria na vitrine com ordem personalizada
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {/* Category Configuration */}
        {categoryOrganizationEnabled && (
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <h3 className="text-lg font-semibold mb-4">Configurações de Categoria</h3>
              
              <div className="space-y-3">
                {categories.map((category, index) => (
                  <div
                    key={category.name}
                    className={`flex items-center gap-4 p-3 border rounded-lg transition-all ${
                      draggedIndex === index ? 'opacity-50' : ''
                    } ${category.enabled ? 'bg-background' : 'bg-muted/50'}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    {/* Drag Handle */}
                    <div className="cursor-move text-muted-foreground">
                      <GripVertical className="h-5 w-5" />
                    </div>

                    {/* Category Name */}
                    <div className="flex-1 font-medium">
                      {category.name}
                    </div>

                    {/* Max Items Input */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Máx. itens:</span>
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={category.maxItems}
                        onChange={(e) => updateCategoryMaxItems(index, parseInt(e.target.value) || 1)}
                        className="w-16 h-8 text-center"
                        disabled={!category.enabled}
                      />
                    </div>

                    {/* Visibility Toggle */}
                    <div className="flex items-center gap-2">
                      {category.enabled ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      <Switch
                        checked={category.enabled}
                        onCheckedChange={(enabled) => updateCategoryEnabled(index, enabled)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <p>• Arraste as categorias para reordenar</p>
                <p>• Configure o número máximo de produtos por categoria</p>
                <p>• Use o switch para mostrar/ocultar categorias na vitrine</p>
                <p>• Produtos sem categoria ou de categorias desabilitadas aparecerão no final</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            Salvar Configurações
          </Button>
        </div>
      </form>
    </Form>
  );
}